import axios from 'axios';

const API_BASE_URL = 'https://api.x-perienc.es/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect to login if it's a 401 AND it's NOT the login endpoint returning the 401
    const originalRequest = error.config;
    if (error.response?.status === 401 && originalRequest.url !== '/auth/login') {
      localStorage.removeItem('token');
      localStorage.removeItem('creator');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface Creator {
  id: string;
  alias: string;
  name: string;
  email: string;
  phone?: string;
  avatar_url?: string;
  country?: string;
  description?: string;
  app?: string;
  url_app?: string;
  url_youtube?: string;
  url_instagram?: string;
  url_other?: string;
  rating?: number;
  credits?: number;
  views?: number;
  votes?: number;
  hide?: number;
  isTrusted?: number | boolean;
  role?: 'creator' | 'manager' | 'administrator';
  status?: 'pending' | 'approved' | 'rejected' | 'banned';
  creation_date?: string;
  last_access?: string;
  emailVerified?: boolean;
}

export interface XperienceMedia {
  id?: string;
  xperience?: string;
  media?: number;
  url?: string;
  posterUrl?: string;
  title?: string;
  description?: string;
  createdDate?: string;
  type?: number;
  format?: number;
  order?: number;
  rating?: number;
  category?: number;
  orden?: number;
  runtime?: number;
  needToBeDownloaded?: number;
  isPublic?: number;
  isPremium?: number;
  hide?: number;
  credits?: number;
  status?: 'pending' | 'approved' | 'rejected';
  creator?: string;
  longitude?: number | string | null;
  latitude?: number | string | null;
  location?: {
    type: 'Point';
    coordinates: [number, number];
  } | null;
  mediaDetails?: Media;
  Xperience?: {
    id: string;
    title: string;
    creatorUser?: {
      id: string;
      name: string;
      alias: string;
      email: string;
    }
  };
}

// Type and Format enums for XperienceMedia
export const XPERIENCE_TYPES: { [key: number]: string } = {
  1: 'Image',
  2: 'Video',
  3: 'Audio',
  4: '360 Photo',
  5: '360 Video'
};

export const XPERIENCE_FORMATS: { [key: number]: string } = {
  1: 'MP4',
  2: 'WebM',
  3: 'MOV',
  4: 'JPEG',
  5: 'PNG',
  6: 'GIF',
  7: 'WebP',
  8: 'MP3',
  9: 'WAV',
  10: 'AAC'
};

export interface XperienceTag {
  id: number;
  tag: string;
}

export interface XperienceScene {
  id: string;
  xperience: string;
  title: string;
  displayOrder?: number;
  order?: number;
  media?: XperienceMedia[];
  items?: Array<{
    id: number;
    sceneId: string;
    mediaId: string;
    displayOrder: number;
    media?: XperienceMedia;
  }>;
  createdAt?: string;
  updatedAt?: string;
}

export interface Xperience {
  id: string;
  title: string;
  description?: string;
  status?: 'pending' | 'approved' | 'rejected' | 'banned';
  views?: number;
  likes_count?: number;
  dislikes_count?: number;
  rating?: number;
  category?: string;
  thumbnail?: string;
  duration?: number;
  userId: string;
  createdDate?: string;
  createdAt: string;
  updatedAt: string;
  isPublic?: number;
  password?: string;
  primaryLongitude?: number | string;
  primaryLatitude?: number | string;
  primaryLocarion?: {
    type: 'Point';
    coordinates: [number, number];
  } | null;
  Media?: Media[];
  mediaLinks?: XperienceMedia[];
  scenes?: XperienceScene[];
  tags?: XperienceTag[];
}

export interface XperienceEvent {
  id: string;
  creator: string;
  title: string;
  description?: string;
  eventType?: string;
  locationName?: string;
  longitude?: number | string | null;
  latitude?: number | string | null;
  location?: {
    type: 'Point';
    coordinates: [number, number];
  } | null;
  eventDate?: string | null;
  coverUrl?: string | null;
  status?: 'pending' | 'approved' | 'rejected';
  hide?: number;
  isPublic?: number;
  password?: string | null;
  createdAt?: string;
  updatedAt?: string;
  creatorAlias?: string;
  xperienceIds?: string[];
  xperiences?: Xperience[];
  xperienceCount?: number;
  mediaCount?: number;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface XperiencesQuery {
  page?: number;
  limit?: number;
  search?: string;
  scope?: 'mine' | 'all';
}

export interface Media {
  id: number;
  filename: string;
  filepath: string;
  type: 'image' | 'video' | 'audio';
  mimeType: string;
  size: number;
  width?: number;
  height?: number;
  duration?: number;
  experienceId: string;
  userId: string;
  createdAt: string;
  XperienceMedia?: {
    posterUrl?: string;
  };
}

export interface UploadProgressInfo {
  progress: number;
  loadedBytes: number;
  totalBytes?: number;
  speedBytesPerSecond?: number;
  estimatedSecondsRemaining?: number;
}

export type UploadProgressHandler = (progress: number, info: UploadProgressInfo) => void;

const createUploadProgressReporter = (
  fallbackTotalBytes: number | undefined,
  onProgress?: UploadProgressHandler
) => {
  const startedAt = Date.now();

  return (progressEvent: { loaded: number; total?: number }) => {
    if (!onProgress) return;

    const totalBytes = progressEvent.total || fallbackTotalBytes || 0;
    const loadedBytes = progressEvent.loaded || 0;
    const elapsedSeconds = Math.max((Date.now() - startedAt) / 1000, 0.001);
    const speedBytesPerSecond = loadedBytes > 0 ? loadedBytes / elapsedSeconds : 0;
    const estimatedSecondsRemaining = totalBytes > loadedBytes && speedBytesPerSecond > 0
      ? (totalBytes - loadedBytes) / speedBytesPerSecond
      : undefined;
    const percentCompleted = totalBytes > 0
      ? Math.min(100, Math.round((loadedBytes * 100) / totalBytes))
      : 0;

    onProgress(percentCompleted, {
      progress: percentCompleted,
      loadedBytes,
      totalBytes,
      speedBytesPerSecond,
      estimatedSecondsRemaining
    });
  };
};

export interface Country {
  code: string;
  name: string;
  full_name: string;
  iso3: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  alias: string;
  email: string;
  password: string;
  acceptedTerms: boolean;
}

// Auth
export const login = (credentials: LoginCredentials) => 
  api.post<{ token: string; creator: Creator; isProfileComplete: boolean }>('/auth/login', credentials);

export const register = (data: RegisterData) => 
  api.post<{ message: string; email: string; emailSent?: boolean }>('/auth/register', data);

export const getProfile = () => 
  api.get<{ creator: Creator; isProfileComplete: boolean }>('/auth/profile');

export const updateProfile = (data: Partial<Creator>) => 
  api.put<{ message: string; creator: Creator; isProfileComplete: boolean }>('/auth/profile', data);

export const changePassword = (currentPassword: string, newPassword: string) =>
  api.post('/auth/change-password', { currentPassword, newPassword });

export const forgotPassword = (email: string) =>
  api.post<{ message: string; token?: string }>('/auth/forgot-password', { email });

export const resetPassword = (token: string, newPassword: string) =>
  api.post('/auth/reset-password', { token, newPassword });

export const verifyEmail = (token: string) =>
  api.get<{ message: string; verified: boolean; alreadyVerified?: boolean }>(`/auth/verify-email?token=${token}`);

export const resendVerificationEmail = (email: string) =>
  api.post<{ message: string; retryAfterSeconds?: number }>('/auth/resend-verification', { email });

// Countries
export const getCountries = () => 
  api.get<Country[]>('/countries');

// Xperiences
export const getXperiences = (params?: XperiencesQuery) => 
  api.get<{ xperiences: Xperience[]; count?: number; pagination?: PaginationMeta }>('/experiences', { params });

export const getXperience = (id: string) => 
  api.get<{ xperience: Xperience }>(`/experiences/${id}`);

export const createXperience = (data: Partial<Xperience>) => 
  api.post<{ xperience: Xperience }>('/experiences', data);

export const updateXperience = (id: string, data: Partial<Xperience>) => 
  api.put<{ xperience: Xperience }>(`/experiences/${id}`, data);

export const emailPrivateXperienceQR = (id: string) => 
  api.post<{ message: string }>(`/experiences/${id}/email-qr`);

export const deleteXperience = (id: string) => 
  api.delete(`/experiences/${id}`);

export const reorderXperienceMedia = (id: string, items: { id: string, order: number }[]) => 
  api.put(`/experiences/${id}/media/reorder`, { items });

export const createXperienceScene = (xperienceId: string, data: { title: string }) =>
  api.post<{ scene: XperienceScene }>(`/experiences/${xperienceId}/scenes`, data);

export const updateXperienceScene = (xperienceId: string, sceneId: string, data: Partial<XperienceScene>) =>
  api.put<{ scene: XperienceScene }>(`/experiences/${xperienceId}/scenes/${sceneId}`, data);

export const deleteXperienceScene = (xperienceId: string, sceneId: string) =>
  api.delete<{ message: string }>(`/experiences/${xperienceId}/scenes/${sceneId}`);

export const reorderXperienceScenes = (xperienceId: string, items: { id: string; order: number }[]) =>
  api.put<{ message: string }>(`/experiences/${xperienceId}/scenes/reorder`, { items });

export const addMediaToXperienceScene = (xperienceId: string, sceneId: string, mediaId: string) =>
  api.post<{ scene: XperienceScene }>(`/experiences/${xperienceId}/scenes/${sceneId}/media`, { mediaId });

export const reorderXperienceSceneMedia = (xperienceId: string, sceneId: string, items: { mediaId: string; order: number }[]) =>
  api.put<{ message: string }>(`/experiences/${xperienceId}/scenes/${sceneId}/media/reorder`, { items });

export const removeMediaFromXperienceScene = (xperienceId: string, sceneId: string, mediaId: string) =>
  api.delete<{ message: string }>(`/experiences/${xperienceId}/scenes/${sceneId}/media/${mediaId}`);

// Events
export const getEvents = () =>
  api.get<{ events: XperienceEvent[] }>('/events');

export const getEvent = (id: string) =>
  api.get<{ event: XperienceEvent }>(`/events/${id}`);

export const createEvent = (data: Partial<XperienceEvent>) =>
  api.post<{ event: XperienceEvent }>('/events', data);

export const updateEvent = (id: string, data: Partial<XperienceEvent>) =>
  api.put<{ event: XperienceEvent }>(`/events/${id}`, data);

export const deleteEvent = (id: string) =>
  api.delete(`/events/${id}`);

export const emailPrivateEventQR = (id: string) =>
  api.post<{ message: string }>(`/events/${id}/email-qr`);

export const uploadEventPoster = (file: File, previousCoverUrl?: string | null, onProgress?: UploadProgressHandler) => {
  const formData = new FormData();
  formData.append('poster', file);
  if (previousCoverUrl) {
    formData.append('previousCoverUrl', previousCoverUrl);
  }
  const reportUploadProgress = createUploadProgressReporter(file.size, onProgress);

  return api.post<{ message: string; coverUrl: string; width: number; height: number }>('/events/poster', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 0,
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
    onUploadProgress: reportUploadProgress,
  });
};

// Media
export const uploadMedia = (experienceId: string, file: File, onProgress?: UploadProgressHandler) => {
  const formData = new FormData();
  formData.append('media', file);
  const reportUploadProgress = createUploadProgressReporter(file.size, onProgress);
  
  return api.post<{ media: Media }>(`/media/upload/${experienceId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 0,
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
    onUploadProgress: reportUploadProgress,
  });
};

export const deleteMedia = (id: number) => 
  api.delete(`/media/${id}`);

// Xperience Media
export const getXperienceMedia = (id: string) =>
  api.get<{ xperienceMedia: XperienceMedia }>(`/xperience-media/${id}`);

export const updateXperienceMedia = (id: string, data: Partial<XperienceMedia>) =>
  api.put<{ xperienceMedia: XperienceMedia }>(`/xperience-media/${id}`, data);

export const deleteXperienceMedia = (id: string) =>
  api.delete<{ message: string }>(`/xperience-media/${id}`);

export const createXperienceMedia = (data: Partial<XperienceMedia>) =>
  api.post<{ xperienceMedia: XperienceMedia }>('/xperience-media', data);

// Avatar Upload
export const uploadAvatar = (file: File, onProgress?: UploadProgressHandler) => {
  const formData = new FormData();
  formData.append('avatar', file);
  const reportUploadProgress = createUploadProgressReporter(file.size, onProgress);
  
  return api.post<{ message: string; avatarPath: string }>('/auth/upload-avatar', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 0,
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
    onUploadProgress: reportUploadProgress,
  });
};

// Xperience Media Uploads
export const uploadXperienceMediaPoster = (xperienceMediaId: string, file: File, onProgress?: UploadProgressHandler) => {
  const formData = new FormData();
  formData.append('poster', file);
  const reportUploadProgress = createUploadProgressReporter(file.size, onProgress);
  
  return api.post<{ message: string; posterUrl: string }>(`/xperience-media/${xperienceMediaId}/upload-poster`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 0,
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
    onUploadProgress: reportUploadProgress,
  });
};

export const uploadXperienceMediaFile = (xperienceMediaId: string, file: File, onProgress?: UploadProgressHandler) => {
  const formData = new FormData();
  formData.append('file', file);
  const reportUploadProgress = createUploadProgressReporter(file.size, onProgress);
  
  return api.post<{ message: string; fileUrl: string }>(`/xperience-media/${xperienceMediaId}/upload-file`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 0, // No timeout for large file uploads
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
    onUploadProgress: reportUploadProgress,
  });
};

// Lookup interfaces
export interface LookupItem {
  id: number;
  name: string;
  description?: string;
}

// Lookups - Fetch from database
export const getXperienceTypes = () =>
  api.get<{ types: LookupItem[] }>('/lookups/types');

export const getXperienceFormats = () =>
  api.get<{ formats: LookupItem[] }>('/lookups/formats');

export const getXperienceCategories = () =>
  api.get<{ categories: LookupItem[] }>('/lookups/categories');

export const getXperienceTags = () =>
  api.get<{ tags: LookupItem[] }>('/lookups/tags');

// Moderation
export const getMediaForModeration = (xperienceId?: string, status?: string) => {
  const query = new URLSearchParams();
  if (xperienceId) query.append('xperienceId', xperienceId);
  if (status) query.append('status', status);
  const queryString = query.toString() ? `?${query.toString()}` : '';
  return api.get<{ media: XperienceMedia[] }>(`/moderation/media${queryString}`);
};

export const updateMediaModerationStatus = (id: string, status: 'pending' | 'approved' | 'rejected') =>
  api.put<{ message: string; media: XperienceMedia }>(`/moderation/media/${id}/status`, { status });

export const getAdminXperiences = (status?: string, params?: XperiencesQuery) =>
  api.get<{ xperiences: Xperience[]; pagination?: PaginationMeta }>('/moderation/xperiences', {
    params: {
      ...(status ? { status } : {}),
      ...(params || {})
    }
  });

export const updateAdminXperienceStatus = (id: string, status: 'pending' | 'approved' | 'rejected') =>
  api.put<{ message: string; xperience: Xperience }>(`/moderation/xperiences/${id}/status`, { status });

// Generate JSON
export const generateCreatorsJSON = () => api.post<{ success: boolean; message: string }>('/generate/creators');
export const generateXperiencesJSON = () => api.post<{ success: boolean; message: string }>('/generate/xperiences');

export default api;

// Admin Creators
export type AdminCreatorSortField = 'alias' | 'email' | 'emailVerified' | 'hide' | 'isTrusted' | 'rating' | 'views' | 'createdAt' | 'role' | 'status';
export type SortDirection = 'asc' | 'desc';

export interface AdminCreatorsQuery {
  page?: number;
  limit?: number;
  sortBy?: AdminCreatorSortField;
  sortDir?: SortDirection;
}

export interface AdminCreatorsPaginatedResponse {
  items: Creator[];
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
    sortBy: AdminCreatorSortField;
    sortDir: SortDirection;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
}

export const getAdminCreators = (params?: AdminCreatorsQuery) =>
  api.get<AdminCreatorsPaginatedResponse | Creator[]>('/admin/creators', { params });
export const updateCreatorRole = (id: string, role: string) => api.put<{message: string}>(`/admin/creators/${id}/role`, { role });
export const updateCreatorStatus = (id: string, status: string) => api.put<{message: string}>(`/admin/creators/${id}/status`, { status });
export const updateCreatorTrust = (id: string, isTrusted: boolean) => api.put<{message: string}>(`/admin/creators/${id}/is-trusted`, { isTrusted });
